// Shared fetch wrapper used by every page in the portal.
//
// Two things it does that hand-rolled fetch() doesn't:
//   1. CSRF — pulls the `csrftoken` cookie Django sets and reflects it back
//      in the X-CSRFToken header. DRF's SessionAuthentication rejects unsafe
//      methods without it.
//   2. Errors — non-2xx responses throw a typed `ApiError` carrying the parsed
//      body (so views can show server-side validation messages directly) and
//      surface 401s so the auth context can route back to /signin.

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, body: unknown, message?: string) {
    super(message ?? deriveMessage(body) ?? `Request failed with ${status}`);
    this.status = status;
    this.body = body;
  }
}

/**
 * Pull a user-readable message out of an error body. Handles three shapes:
 *  - {detail: "..."}                          (plain DRF / our /auth/ views)
 *  - {success: false, errors: [{field: [...]]}]} (core.renderers.CustomRenderer)
 *  - plain string
 */
function deriveMessage(body: unknown): string | null {
  if (!body) return null;
  if (typeof body === "string") return body;
  if (typeof body !== "object") return null;
  const obj = body as Record<string, unknown>;

  if (typeof obj.detail === "string") return obj.detail;

  if (Array.isArray(obj.errors)) {
    const first = obj.errors[0];
    if (typeof first === "string") return first;
    if (first && typeof first === "object") {
      for (const [field, value] of Object.entries(first as Record<string, unknown>)) {
        const v = Array.isArray(value) ? value[0] : value;
        if (typeof v === "string") return field === "detail" ? v : `${field}: ${v}`;
      }
    }
  }
  return null;
}

// Django's `core.renderers.CustomRenderer` wraps successful responses but the
// /flights/auth/* endpoints don't go through DRF's renderer. We accept both
// shapes — `{data: ...}` and plain bodies.
function unwrap<T>(body: unknown): T {
  if (body && typeof body === "object" && "data" in body) {
    return (body as { data: T }).data;
  }
  return body as T;
}

function getCookie(name: string): string | null {
  const match = document.cookie
    .split(";")
    .map((c) => c.trim())
    .find((c) => c.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.split("=").slice(1).join("=")) : null;
}

interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  // If true, throws ApiError(401) without trying to redirect.
  silent401?: boolean;
}

async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const method = opts.method ?? "GET";
  const isUnsafe = method !== "GET";

  const headers: Record<string, string> = {
    Accept: "application/json",
  };
  if (opts.body !== undefined) {
    headers["Content-Type"] = "application/json";
  }
  if (isUnsafe) {
    const csrf = getCookie("csrftoken");
    if (csrf) headers["X-CSRFToken"] = csrf;
  }

  const res = await fetch(path, {
    method,
    credentials: "include",
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
  });

  // 204 No Content
  if (res.status === 204) return undefined as T;

  const text = await res.text();
  let parsed: unknown = null;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = text;
    }
  }

  if (!res.ok) {
    throw new ApiError(res.status, parsed);
  }
  return unwrap<T>(parsed);
}

export const api = {
  get: <T>(path: string, opts: Omit<RequestOptions, "method" | "body"> = {}) =>
    request<T>(path, { ...opts, method: "GET" }),
  post: <T>(path: string, body?: unknown, opts: Omit<RequestOptions, "method" | "body"> = {}) =>
    request<T>(path, { ...opts, method: "POST", body }),
  // Hits /auth/csrf/ so Django plants the csrftoken cookie before we
  // make any POST. Idempotent; safe to call on every app boot.
  primeCsrf: () => request<{ csrfToken: string }>("/auth/csrf/"),
};
